'use client'

import { ImageIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { IProjectInfo } from '@/hooks/subscriptions/use-workspaces'
import Image from 'next/image'

interface ProjectsListVerticalProps {
  projects: IProjectInfo[]
  className?: string
}

/**
 * Vertical projects list component for pricing-v3 (Pure UI - no actions)
 * Displays projects in a vertical list with preview thumbnail, name, status, and preview URL
 * Expandable to show all projects
 */
export function ProjectsListVertical({
  projects,
  className,
}: ProjectsListVerticalProps) {
  /**
   * Get the preview URL for a project
   * Priority: own_domain > new_domain > default_domain > deploy_url
   */
  const getPreviewUrl = (project: IProjectInfo): string | null => {
    return (
      project.own_domain ||
      project.new_domain ||
      project.default_domain ||
      project.deploy_url ||
      null
    )
  }

  /**
   * Get the thumbnail URL for a project
   */
  const getThumbnailUrl = (project: IProjectInfo): string | null => {
    return project.thumbnail || null
  }

  if (projects.length === 0) {
    return (
      <div className={cn('flex items-center justify-center p-4', className)}>
        <p className="text-sm text-white/60">No projects in this workspace</p>
      </div>
    )
  }

  return (
    <div className={cn('', className)}>
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-sm font-semibold text-white">
          Projects ({projects.length})
        </h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
        {projects.map((project) => {
          const previewUrl = getPreviewUrl(project)
          const thumbnailUrl = getThumbnailUrl(project)
          const displayUrl = previewUrl
            ? previewUrl.replace(/^https?:\/\//, '').slice(0, 40)
            : 'No preview URL'

          return (
            <div
              key={project._id}
              className={cn(
                'w-full flex items-center gap-2 p-1',
                'bg-white/5',
                'border border-white/10',
                'rounded-lg',
              )}
            >
              {/* Preview thumbnail or placeholder */}
              <div className="flex-shrink-0 w-20 h-14 rounded-md overflow-hidden bg-white/10 border border-white/20 flex items-center justify-center">
                {thumbnailUrl ? (
                  <Image
                    src={thumbnailUrl}
                    alt={project.project_name}
                    width={80}
                    height={56}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ImageIcon className="size-5 text-white/40" />
                )}
              </div>

              {/* Project info */}
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-semibold text-white line-clamp-2">
                  {project.project_name}
                </p>
                {previewUrl && (
                  <p className="text-xs text-white/60 truncate mt-0.5">
                    {displayUrl}
                  </p>
                )}
                {project.status && (
                  <div className="flex items-center gap-1 mt-1">
                    <div
                      className={cn('size-1.5 rounded-full', {
                        'bg-green-400': project.status === 'live',
                        'bg-yellow-400': project.status === 'in progress',
                        'bg-gray-400': project.status === 'draft',
                      })}
                    />
                    <p
                      className={cn('text-2xs capitalize', {
                        'text-green-400': project.status === 'live',
                        'text-yellow-400': project.status === 'in progress',
                        'text-gray-400': project.status === 'draft',
                      })}
                    >
                      {project.status}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
